import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/wallet/LegalPage";

export const Route = createFileRoute("/risk")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Security & Risk Disclosure — Cobalt Wallet" },
      {
        name: "description",
        content:
          "How Cobalt Wallet secures your keys, what the app cannot protect you from, and the risks of holding XRP and XRPL tokens.",
      },
      { property: "og:title", content: "Security & Risk Disclosure — Cobalt Wallet" },
      {
        property: "og:description",
        content: "AES-256-GCM local vault, passkey or passcode unlock, and an honest list of the risks.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://cobaltwalletxrpl.com/risk" }],
  }),
  component: RiskPage,
});

function RiskPage() {
  return (
    <LegalPage
      eyebrow="Security"
      title="Security & Risk Disclosure"
      updated="30 August 2026"
      intro="This page describes exactly how Cobalt Wallet protects your keys, and the risks that no wallet design can remove."
    >
      <LegalSection heading="How your keys are protected">
        <p>
          Keys are derived from a BIP39 recovery phrase generated in your browser using the operating
          system's cryptographic random source, with an optional 25th word you choose.
        </p>
        <p>
          The vault is encrypted with AES-256-GCM. The encryption key comes from your device passkey
          (WebAuthn PRF) where supported, or from PBKDF2-SHA256 with 600,000 iterations over your
          6-digit passcode. Keys are decrypted only in memory, only while unlocked.
        </p>
        <p>
          Signing and submission happen locally against public XRPL nodes. There is no server, no
          account and no key escrow.
        </p>
      </LegalSection>

      <LegalSection heading="In-app guards">
        <p>
          Auto-lock after inactivity, hold-to-confirm signing, full recipient address verification,
          large-transfer warnings, trustline risk labels, and an explicit fee disclosure before every
          signature.
        </p>
      </LegalSection>

      <LegalSection heading="What we cannot protect you from">
        <p>
          A compromised device, malware, a malicious browser extension, a screen recorder, or anyone
          who obtains your recovery phrase. Phrase theft is total and irreversible loss.
        </p>
        <p>
          A 6-digit passcode protects against casual device access, not against an attacker who has
          copied your encrypted vault and can attempt offline guessing. Your recovery-phrase backup
          and device security remain the real defence.
        </p>
        <p>
          Sending to a wrong or attacker-supplied address. XRPL transactions are final and cannot be
          reversed.
        </p>
      </LegalSection>

      <LegalSection heading="XRPL-specific risks">
        <p>
          Accounts require a base reserve in XRP that cannot be spent, and each trustline or object
          adds an owner reserve. Adding a trustline to an unverified issuer can expose you to
          worthless or malicious tokens, freezes and clawback where enabled.
        </p>
        <p>
          DEX swaps execute against live order books; thin liquidity can produce poor execution.
          Third-party price and ETF data may be stale or unavailable.
        </p>
      </LegalSection>

      <LegalSection heading="Market risk">
        <p>
          XRP and XRPL tokens are volatile and can lose all value. Nothing in this app is investment
          advice. Only hold what you can afford to lose.
        </p>
      </LegalSection>

      <LegalSection heading="Reporting a vulnerability">
        <p>
          If you believe you have found a security issue, report it privately to the operator of this
          deployment before disclosing it publicly. Please do not include your recovery phrase, keys
          or passcode in any report.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
